import { useState } from 'react';
import type { PersonSummaryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { Button } from '@/components/ui';
import { SendMessageDialog } from '../communications/send-message-dialog';

/**
 * Shown once at least one row is selected.
 *
 * A CSV export of the rows currently loaded on screen (not the whole
 * tenant — list pagination means later rows aren't fetched yet), a bulk
 * archive that loops the existing single-person archive endpoint, and a
 * message to the selection sent via a `PERSON_IDS` campaign — the same
 * `SendMessageDialog` a single profile's "send a message" action uses.
 */
export function PeopleBulkBar({
  selectedIds,
  people,
  onArchived,
}: {
  readonly selectedIds: ReadonlySet<string>;
  readonly people: readonly PersonSummaryResponse[];
  readonly onArchived: () => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [sendNotice, setSendNotice] = useState<string | null>(null);

  if (selectedIds.size === 0) return null;

  const selected = people.filter((person) => selectedIds.has(person.id));

  function exportCsv(): void {
    const header = ['Name', 'Email', 'Phone', 'Status'];
    const rows = selected.map((person) => [
      person.displayName,
      person.primaryEmail ?? '',
      person.primaryPhone ?? '',
      person.status,
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'people.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  async function archiveSelected(): Promise<void> {
    const reason = window.prompt(t('people.bulk.archiveReason'));
    if (!reason?.trim()) return;

    setIsArchiving(true);
    setArchiveError(null);
    const failures: string[] = [];
    for (const person of selected) {
      try {
        await peopleApi.archive(person.id, reason.trim());
      } catch {
        failures.push(person.displayName);
      }
    }
    setIsArchiving(false);
    if (failures.length > 0) {
      setArchiveError(t('people.bulk.archiveFailed', { values: { names: failures.join(', ') } }));
    }
    onArchived();
  }

  return (
    <div className="people-bulk-bar" role="status">
      <span className="people-bulk-bar-count">
        {t('people.bulk.selected', { count: selectedIds.size })}
      </span>
      <div className="people-bulk-bar-actions">
        <Button type="button" variant="ghost" onClick={exportCsv}>
          {t('common.actions.export')}
        </Button>
        {can('people.person.archive') && (
          <Button
            type="button"
            variant="secondary"
            loading={isArchiving}
            onClick={() => void archiveSelected()}
          >
            {t('people.bulk.archiveSelected')}
          </Button>
        )}
        {can('communications.campaign.create') && can('communications.campaign.send') && (
          <Button type="button" variant="secondary" onClick={() => setIsSendingMessage(true)}>
            {t('communications.sendMessage.action')}
          </Button>
        )}
      </div>
      <span className="people-bulk-bar-hint">{t('people.bulk.exportHint')}</span>
      {archiveError && <span className="people-bulk-bar-hint">{archiveError}</span>}
      {sendNotice && <span className="people-bulk-bar-hint">{sendNotice}</span>}

      {isSendingMessage && (
        <SendMessageDialog
          onClose={() => setIsSendingMessage(false)}
          onSent={() => {
            setIsSendingMessage(false);
            setSendNotice(t('communications.sendMessage.sent'));
          }}
          recipientIds={selected.map((person) => person.id)}
          recipientLabel={t('people.bulk.selected', { count: selected.length })}
        />
      )}
    </div>
  );
}
